/**
 * Sound FX for CRYPTOBOOM — pure Web Audio API (no external MP3 assets).
 * Exposes a small API: setEnabled(bool), play(name).
 * Silently no-ops if AudioContext is unavailable or disabled.
 */

let ctx = null;
let enabled = true;

function ensureContext() {
  if (ctx || typeof window === "undefined") return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch (_) {
    ctx = null;
  }
  return ctx;
}

export function setEnabled(flag) {
  enabled = !!flag;
  try { localStorage.setItem("cb_sfx", enabled ? "1" : "0"); } catch (_) { /* noop */ }
}

export function isEnabled() {
  try {
    const v = localStorage.getItem("cb_sfx");
    if (v !== null) enabled = v === "1";
  } catch (_) { /* noop */ }
  return enabled;
}

// Initialize from storage on load
isEnabled();

function envelope(gain, t0, peak, attack, release) {
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + release);
}

function blip({ freq = 440, type = "sine", duration = 0.12, peak = 0.15, sweep = 0 }) {
  if (!enabled) return;
  const c = ensureContext();
  if (!c) return;
  try {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, c.currentTime);
    if (sweep) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(40, freq + sweep),
        c.currentTime + duration,
      );
    }
    envelope(gain, c.currentTime, peak, duration * 0.08, duration * 0.92);
    osc.connect(gain).connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + duration + 0.05);
  } catch (_) { /* noop */ }
}

function noiseBurst({ duration = 0.25, peak = 0.25, lowpass = 800 }) {
  if (!enabled) return;
  const c = ensureContext();
  if (!c) return;
  try {
    const bufferSize = Math.floor(c.sampleRate * duration);
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = lowpass;
    const gain = c.createGain();
    envelope(gain, c.currentTime, peak, 0.01, duration);
    src.connect(filter).connect(gain).connect(c.destination);
    src.start();
    src.stop(c.currentTime + duration + 0.05);
  } catch (_) { /* noop */ }
}

const PRESETS = {
  flap:      () => blip({ freq: 620, type: "triangle", duration: 0.08, peak: 0.12, sweep: 80 }),
  coin:      () => blip({ freq: 880, type: "square",   duration: 0.12, peak: 0.1,  sweep: 220 }),
  shoot:     () => blip({ freq: 520, type: "sawtooth", duration: 0.08, peak: 0.08, sweep: -180 }),
  hit:       () => noiseBurst({ duration: 0.2, peak: 0.25, lowpass: 1200 }),
  explosion: () => {
    noiseBurst({ duration: 0.45, peak: 0.35, lowpass: 900 });
    blip({ freq: 120, type: "sawtooth", duration: 0.35, peak: 0.18, sweep: -60 });
  },
  gameover:  () => {
    blip({ freq: 440, type: "triangle", duration: 0.18, peak: 0.18 });
    setTimeout(() => blip({ freq: 330, type: "triangle", duration: 0.18, peak: 0.16 }), 140);
    setTimeout(() => blip({ freq: 220, type: "triangle", duration: 0.32, peak: 0.16 }), 280);
  },
  powerup:   () => {
    blip({ freq: 440, type: "square", duration: 0.1,  peak: 0.12, sweep: 220 });
    setTimeout(() => blip({ freq: 660, type: "square", duration: 0.12, peak: 0.14, sweep: 200 }), 80);
  },
  wheel_tick: () => blip({ freq: 720, type: "square", duration: 0.04, peak: 0.06 }),
  wheel_win:  () => {
    blip({ freq: 523, type: "triangle", duration: 0.14, peak: 0.18 });
    setTimeout(() => blip({ freq: 659, type: "triangle", duration: 0.14, peak: 0.2 }), 120);
    setTimeout(() => blip({ freq: 784, type: "triangle", duration: 0.26, peak: 0.22 }), 240);
  },
};

export function play(name) {
  const fn = PRESETS[name];
  if (fn) fn();
}

export default { play, setEnabled, isEnabled };
